/**
 * <rlhf-session-bar> — session progress + the export/close actions.
 *
 * Reads the folded state from the SessionStore for its counts (annotations,
 * grades, preferences, rulings) and shows progress as `graded / total`. "Export"
 * dispatches a composed `rlhf-session-bar:export` event (the host writes the OKF
 * bundle — the element never touches fs); "Abschließen" appends a `session.Closed`
 * event and dispatches `rlhf-session-bar:close`.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, styleMap, type TemplateResult } from "@platform/web-kit/render"
import type { SessionState } from "@platform/rlhf-core"
import { RlhfSessionStoreToken, type RlhfSessionStore } from "./store"
import { systemClock, type RlhfClock } from "./clock"

export class SessionBarVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private readonly storeState: ReadonlySignal<SessionState>

  constructor(
    private readonly store: RlhfSessionStore,
    readonly total: ReadonlySignal<number>,
    readonly label: ReadonlySignal<string>,
    private readonly actor: ReadonlySignal<string>,
    private readonly clock: RlhfClock,
  ) {
    this.storeState = store.state()
    this.renderDeps = [total, label, this.storeState]
  }

  state(): SessionState {
    return this.storeState.value
  }

  /** Append the session-close event (idempotent: no-op once already closed). */
  close(): void {
    if (this.storeState.value.closed) return
    this.store.append({ type: "session.Closed", at: this.clock.now(), actor: this.actor.value })
  }
}

export class RlhfSessionBar extends KitElement<SessionBarVm> {
  readonly #total = signal(0)
  readonly #label = signal("")
  readonly #actor = signal("operator")
  #clock: RlhfClock = systemClock()

  get total(): number {
    return this.#total.value
  }
  set total(v: number) {
    this.#total.set(v)
  }
  get label(): string {
    return this.#label.value
  }
  set label(v: string) {
    this.#label.set(v)
  }
  get actor(): string {
    return this.#actor.value
  }
  set actor(v: string) {
    this.#actor.set(v)
  }
  set clock(c: RlhfClock) {
    this.#clock = c
  }

  protected createViewModel(ctx: KitContext): SessionBarVm {
    return new SessionBarVm(ctx.inject(RlhfSessionStoreToken), this.#total, this.#label, this.#actor, this.#clock)
  }

  #emit(name: string): void {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail: { label: this.#label.value } }))
  }

  protected render(vm: SessionBarVm): TemplateResult {
    const state = vm.state()
    const total = vm.total.value
    const graded = state.grades.length
    const pct = total > 0 ? Math.min(100, Math.round((graded / total) * 100)) : 0
    return html`
      <div class="rlhf-bar" role="group" aria-label="Session">
        ${vm.label.value ? html`<span class="rlhf-bar-label" lang="de">${vm.label.value}</span>` : nothing}
        <span
          class="rlhf-progress"
          role="progressbar"
          aria-valuenow=${graded}
          aria-valuemin="0"
          aria-valuemax=${total > 0 ? total : graded}
          aria-label="Fortschritt"
        >
          <span class="rlhf-progress-track"
            ><span class="rlhf-progress-fill" style=${styleMap({ width: `${pct}%` })}></span
          ></span>
          <span class="rlhf-progress-text" lang="de"
            >${graded}${total > 0 ? html` / ${total}` : nothing} bewertet</span
          >
        </span>
        <span class="rlhf-counts">
          <span class="rlhf-chip-count" lang="de">${state.annotations.length} Annotationen</span>
          <span class="rlhf-chip-count" lang="de">${state.preferences.length} Vergleiche</span>
          <span class="rlhf-chip-count" lang="de">${state.rulings.length} Stimmfragen</span>
        </span>
        ${state.closed ? html`<span class="rlhf-closed" role="status" lang="de">abgeschlossen</span>` : nothing}
        <span class="rlhf-bar-actions">
          <button type="button" class="rlhf-btn rlhf-btn-quiet" data-act="session-export" lang="de">Exportieren</button>
          <button
            type="button"
            class="rlhf-btn rlhf-btn-primary"
            data-act="session-close"
            ?disabled=${state.closed}
            lang="de"
          >
            Abschließen
          </button>
        </span>
      </div>
    `
  }

  protected override actions(vm: SessionBarVm): ActionMap {
    return {
      "session-export": () => this.#emit("rlhf-session-bar:export"),
      "session-close": () => {
        vm.close()
        this.#emit("rlhf-session-bar:close")
      },
    }
  }
}
